/**
 * Faction definitions. Adding a faction is a data change, never a code change:
 * everything downstream (director catalogues, unit rosters, architecture,
 * colour identity, flavour voice) keys off this table.
 */

import { C } from './palette.ts';
import type { ResourceKey } from '../sim/types.ts';

export type ArchitectureStyle =
  | 'stone'
  | 'timber'
  | 'grown'
  | 'bone'
  | 'hide'
  | 'gilded'
  | 'crude'
  | 'burrow'
  | 'fungal'
  | 'coral';

export interface FactionBonus {
  /** Multipliers applied at world init. 1 = unchanged. */
  resourceRate?: Partial<Record<ResourceKey, number>>;
  buildCost?: number;
  buildSpeed?: number;
  wallHp?: number;
  unitHp?: number;
  unitDamage?: number;
  moveSpeed?: number;
  popCap?: number;
  willRegen?: number;
  grainUpkeep?: number;
  /** Max building tier this faction may upgrade to (default 3). */
  maxBuildingTier?: number;
}

export interface FactionDef {
  id: string;
  name: string;
  /**
   * The race, in the plainest words available.
   *
   * The proper names in this setting are deliberately oblique — "The Verdant
   * Choir", "Karrun-Dûr", "The Gutter Congregation" — which is good for the
   * voice and bad for a player choosing a faction for the first time, who
   * mostly wants to know whether they are picking dwarves or ratmen. So every
   * faction carries a flat, familiar label alongside its name, and the UI shows
   * both: the evocative one for atmosphere, this one so the choice is legible.
   * Where two factions share a race, the label says how they differ.
   */
  race: string;
  epithet: string;
  flavour: string;
  /** The one saturated colour this faction is allowed. Banners, trim, FX only. */
  accent: number;
  accentDark: number;
  cloth: number;
  metal: number;
  playable: boolean;
  signature: string;
  signatureText: string;
  architecture: ArchitectureStyle;
  bonuses: FactionBonus;
  /** Unit ids available to this faction, in roughly the order they unlock. */
  roster: string[];
  /** Starting companies at wave 1. */
  startCompanies: string[];
}

export const FACTIONS: FactionDef[] = [
  {
    id: 'ashen',
    name: 'The Ashen Crown',
    race: 'Humans',
    epithet: 'human remnant',
    flavour:
      'What is left of an empire that could not decide whether it was dying, and so did both — slowly, and then all at once.',
    accent: C.gold,
    accentDark: C.goldDeep,
    cloth: 0x4a3f38,
    metal: 0x8c8a83,
    playable: true,
    signature: 'Banner Discipline',
    signatureText:
      'Companies within 6 tiles of a friendly banner recover morale twice as fast and refuse to rout above half strength.',
    architecture: 'stone',
    bonuses: { willRegen: 1.1, buildSpeed: 1.05 },
    roster: [
      'ashen_militia',
      'ashen_spear',
      'ashen_archer',
      'ashen_outrider',
      'ashen_manatarms',
      'ashen_arbalest',
      'ashen_knight',
      'ashen_bellwarden',
      // The mews. Every playable roster gets two wings — see the flying block
      // in `units.ts` for what `UF.Flying` buys and what it does not.
      'ashen_pegasus',
      'ashen_gryphon',
      // The Kindling's line, and the three Ages above it. Common to every
      // house — see the note in `units.ts`.
      'runeguard',
      'voidsworn',
      'stormcaller',
      'ashblade',
      'heartiron_colossus',
      'hollowed',
      'knell_engine',
    ],
    startCompanies: ['ashen_militia', 'ashen_archer'],
  },
  {
    id: 'karrun',
    name: 'Karrun-Dûr',
    race: 'Dwarves',
    epithet: 'the deep dwarves',
    flavour:
      'They did not flee the dark. They went down to meet it, and came back up with better walls.',
    accent: 0xc06a2c,
    accentDark: 0x7a3d15,
    cloth: 0x53372a,
    metal: 0x9a8f7c,
    playable: true,
    signature: 'Deepwrights',
    signatureText:
      'Every building may be upgraded one tier beyond anyone else. Walls cost less stone and have half again the health.',
    architecture: 'stone',
    bonuses: { wallHp: 1.5, maxBuildingTier: 4, moveSpeed: 0.85, resourceRate: { stone: 1.25, iron: 1.2 } },
    roster: [
      'karrun_shieldbearer',
      'karrun_pikeman',
      'karrun_handgonner',
      'karrun_forgeguard',
      'karrun_ballistier',
      'karrun_runesmith',
      'karrun_deepbreaker',
      // Karrun-Dûr keep no beasts. Both of their wings are wound and bolted.
      'karrun_gyre',
      'karrun_ironkite',
      // The Kindling's line, and the three Ages above it. Common to every
      // house — see the note in `units.ts`.
      'runeguard',
      'voidsworn',
      'stormcaller',
      'ashblade',
      'heartiron_colossus',
      'hollowed',
      'knell_engine',
    ],
    startCompanies: ['karrun_shieldbearer', 'karrun_handgonner'],
  },
  {
    id: 'verdant',
    name: 'The Verdant Choir',
    race: 'Wood Elves',
    epithet: 'sylvan elves',
    flavour: 'They sing to things that used to be trees. Sometimes the things answer.',
    accent: C.plagueLight,
    accentDark: 0x4d6320,
    cloth: 0x3c4a2e,
    metal: 0xb2b59c,
    playable: true,
    signature: 'Rootwork',
    signatureText:
      'Growth buildings compound: every completed grove raises the yield of all others. Terrain can be reshaped with Will.',
    architecture: 'grown',
    bonuses: { resourceRate: { timber: 1.3, grain: 1.15 }, unitHp: 0.9, moveSpeed: 1.1 },
    roster: [
      'verdant_glaive',
      'verdant_longbow',
      'verdant_thornguard',
      'verdant_stagrider',
      'verdant_songweaver',
      'verdant_bramblewarden',
      'verdant_owlrider',
      'verdant_thornwyrm',
      // The Kindling's line, and the three Ages above it. Common to every
      // house — see the note in `units.ts`.
      'runeguard',
      'voidsworn',
      'stormcaller',
      'ashblade',
      'heartiron_colossus',
      'hollowed',
      'knell_engine',
    ],
    startCompanies: ['verdant_glaive', 'verdant_longbow'],
  },
  {
    id: 'litany',
    name: 'The Pale Litany',
    race: 'Undead',
    epithet: 'the undead court',
    flavour: 'The court never adjourned. It merely stopped requiring attendance in the flesh.',
    accent: 0x9fb8c8,
    accentDark: 0x4d6472,
    cloth: 0x2f3540,
    metal: 0xb6b4a6,
    playable: true,
    signature: 'Grave Tithe',
    signatureText:
      'Corpses on your ground, friend or foe, rise as Wretches. No grain upkeep — but Souls drain every second you hold the field.',
    architecture: 'bone',
    bonuses: { grainUpkeep: 0, resourceRate: { souls: 1.4 }, unitHp: 0.85 },
    roster: [
      'litany_wretch',
      'litany_boneshield',
      'litany_gravebow',
      'litany_cryptknight',
      'litany_palepriest',
      'litany_charnelox',
      'litany_ossuaryflock',
      'litany_barrowwyrm',
      // The Kindling's line, and the three Ages above it. Common to every
      // house — see the note in `units.ts`.
      'runeguard',
      'voidsworn',
      'stormcaller',
      'ashblade',
      'heartiron_colossus',
      'hollowed',
      'knell_engine',
    ],
    startCompanies: ['litany_boneshield', 'litany_gravebow'],
  },
  {
    id: 'moonbound',
    name: 'The Moonbound',
    race: 'Beastmen',
    epithet: 'beastfolk of the long howl',
    flavour: 'They keep no walls. A wall is a thing you die behind.',
    accent: 0x7f6fb8,
    accentDark: 0x413661,
    cloth: 0x463c34,
    metal: 0x8f8577,
    playable: true,
    signature: 'Night Hunger',
    signatureText:
      'All units gain +25% damage and +15% speed during a siege. Walls cost double and have half health.',
    architecture: 'hide',
    bonuses: { wallHp: 0.5, moveSpeed: 1.2, unitDamage: 1.05, popCap: 1.1 },
    roster: [
      'moon_pack',
      'moon_howler',
      'moon_slinger',
      'moon_shadowclaw',
      'moon_alpha',
      'moon_moonsinger',
      'moon_stormraven',
      'moon_wintering',
      // The Kindling's line, and the three Ages above it. Common to every
      // house — see the note in `units.ts`.
      'runeguard',
      'voidsworn',
      'stormcaller',
      'ashblade',
      'heartiron_colossus',
      'hollowed',
      'knell_engine',
    ],
    startCompanies: ['moon_pack', 'moon_slinger'],
  },
  {
    id: 'vigil',
    name: 'The Gilded Vigil',
    race: 'Humans — Holy Order',
    epithet: 'a holy order, fallen',
    flavour:
      'Nine hundred oaths, and the ninth hundred was the one about not counting the others.',
    accent: 0xe8c477,
    accentDark: 0x9a7429,
    cloth: 0xd8d2c4,
    metal: 0xd9c48b,
    playable: true,
    signature: 'Sacrament',
    signatureText:
      'Powers cost 20% less Will and hit 25% harder. Population cap is 40% lower — every soldier is a relic.',
    architecture: 'gilded',
    bonuses: { popCap: 0.6, willRegen: 1.35, unitHp: 1.25, unitDamage: 1.15, buildCost: 1.2 },
    roster: [
      'vigil_lampbearer',
      'vigil_censer',
      'vigil_pyreknight',
      'vigil_sunbolt',
      'vigil_confessor',
      'vigil_reliquar',
      // The second human roster flies too, and differently — a censer on a
      // chain under a bird, rather than a lance.
      'vigil_censerhawk',
      'vigil_gryphon',
      // The Kindling's line, and the three Ages above it. Common to every
      // house — see the note in `units.ts`.
      'runeguard',
      'voidsworn',
      'stormcaller',
      'ashblade',
      'heartiron_colossus',
      'hollowed',
      'knell_engine',
    ],
    startCompanies: ['vigil_lampbearer', 'vigil_sunbolt'],
  },

  // ── Enemy-only ────────────────────────────────────────────────────────────
  {
    id: 'ironfang',
    name: 'The Ironfang Horde',
    race: 'Orcs & Ogres',
    epithet: 'orcs, ogres, and everything they dragged along',
    flavour: 'They do not besiege. They arrive, and the walls become a formality.',
    accent: 0xa8362c,
    accentDark: 0x5e1a15,
    cloth: 0x3f3129,
    metal: 0x76706a,
    playable: false,
    signature: 'Warcry',
    signatureText: 'Ogres bellow: nearby horde units gain damage and ignore fear for eight seconds.',
    architecture: 'crude',
    bonuses: {},
    roster: [
      'iron_grunt',
      'iron_archer',
      /**
       * These two were in `units.ts` and on nobody's roster.
       *
       * Which made them unreachable content of the quietest kind: the director's
       * *first* counter branch is "archer stacks → shields and pavises", and the
       * only two units in the game with a pavise or a shield-breaking hammer on
       * the Horde's side were not in the pool it filters. `spendOnCounters` fell
       * through to `choice.weight = 0` and bought something else, for ever, and
       * nothing anywhere failed. Found by the reachability sweep in
       * `tests/roster.test.ts`, which exists so this cannot recur.
       */
      'iron_shieldbreaker',
      'iron_pavise',
      'iron_wolfrider',
      'iron_sapper',
      'iron_ogre',
      'iron_ram',
      'iron_wyvern',
      'iron_warlord',
    ],
    startCompanies: [],
  },
  {
    id: 'gutter',
    name: 'The Gutter Congregation',
    race: 'Ratmen',
    epithet: 'ratkin, and their sermons',
    flavour: 'Every one of them is certain it will be the one that survives. Statistically, one is right.',
    accent: 0x9c8f3a,
    accentDark: 0x585123,
    cloth: 0x3a352a,
    metal: 0x6a6459,
    playable: false,
    signature: 'Undermine',
    signatureText: 'Tunnellers emerge *behind* your wall line. Walls do not stop a Congregation, only slow its sermon.',
    architecture: 'burrow',
    bonuses: {},
    roster: [
      'gutter_swarm',
      'gutter_slinger',
      'gutter_tunneller',
      'gutter_belfry',
      'gutter_censerrat',
      'gutter_gnawbeast',
      'gutter_bloatpriest',
    ],
    startCompanies: [],
  },
  {
    id: 'rotmoot',
    name: 'The Rotmoot',
    race: 'Plaguebearers',
    epithet: 'the plague that learned to vote',
    flavour: 'A parliament of sores. Motions carried by acclamation, and by spore.',
    accent: 0x7d9436,
    accentDark: 0x415021,
    cloth: 0x3b3f2c,
    metal: 0x6d6a53,
    playable: false,
    signature: 'Bloom',
    signatureText: 'Rotmoot dead burst, leaving poison ground that your units will happily stand in.',
    architecture: 'fungal',
    bonuses: {},
    roster: [
      'rot_shambler',
      'rot_spewer',
      'rot_blightbearer',
      'rot_bloatbruiser',
      'rot_carrionchoir',
      'rot_pesthulk',
      'rot_sporeherald',
    ],
    startCompanies: [],
  },
  {
    id: 'drowned',
    name: 'The Drowned Court',
    race: 'Dark Elves',
    epithet: 'abyssal, and patient',
    flavour: 'They came inland by a route that is not on any map, and is not, strictly, a route.',
    accent: 0x4a7f9c,
    accentDark: 0x24485c,
    cloth: 0x27333c,
    metal: 0x7d8f93,
    playable: false,
    signature: 'Madness',
    signatureText: 'Fog rolls in. Units caught inside it forget which banner they follow.',
    architecture: 'coral',
    bonuses: {},
    roster: [
      'drowned_thrall',
      'drowned_lamprey',
      'drowned_choir',
      'drowned_stormray',
      'drowned_tideguard',
      'drowned_abyssal',
    ],
    startCompanies: [],
  },
];

const BY_ID = new Map<string, FactionDef>();
for (const f of FACTIONS) BY_ID.set(f.id, f);

export function faction(id: string): FactionDef {
  const f = BY_ID.get(id);
  if (!f) throw new Error(`Unknown faction: ${id}`);
  return f;
}

export function factionIndex(id: string): number {
  return FACTIONS.findIndex((f) => f.id === id);
}

export function factionByIndex(i: number): FactionDef {
  return FACTIONS[i] ?? FACTIONS[0];
}

export const PLAYABLE_FACTIONS = FACTIONS.filter((f) => f.playable);
export const INVADER_FACTIONS = FACTIONS.filter((f) => !f.playable);
